import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSRecursion = () => {
    const basicRecursionCode = `// The Anatomy of Recursion
function factorial(n) {
    // 1. Base Case (The Exit)
    if (n <= 1) return 1;

    // 2. Recursive Case (Breaking down the problem)
    return n * factorial(n - 1);
}

console.log(factorial(5)); // 120`;

    const stackOverflowCode = `//  Danger: Stack Overflow
function count(n) {
    console.log(n);
    count(n + 1); // No base case!
}

// Result: RangeError: Maximum call stack size exceeded`;

    const trampolinePatternCode = `// Production Trick: The Trampoline
// Avoids stack overflow for deep recursion by turning it into a loop.
const trampoline = (fn) => (...args) => {
    let result = fn(...args);
    while (typeof result === 'function') {
        result = result();
    }
    return result;
};

const getSum = (n, sum = 0) => {
    if (n === 0) return sum;
    // Return a function to be called by the trampoline (deferred execution)
    return () => getSum(n - 1, sum + n);
};

const safeSum = trampoline(getSum);
console.log(safeSum(100000)); // No Stack Overflow!`;

    const treeTraversalCode = `// Practical Use: Searching a Nested File System
const fileSystem = {
    name: 'root',
    files: ['index.html'],
    folders: [
        { name: 'src', files: ['app.js', 'utils.js'], folders: [] },
        { name: 'assets', files: ['logo.png'], folders: [] }
    ]
};

function listAllFiles(node, allFiles = []) {
    // Collect files in current folder
    allFiles.push(...node.files);

    // Recursively process subfolders
    node.folders.forEach(folder => listAllFiles(folder, allFiles));

    return allFiles;
}

console.log(listAllFiles(fileSystem));`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Algorithms & Data Structures</div>
                <h1>Recursion & The Call Stack</h1>
                <p className="hero-lead">
                    Recursion is a programming technique where a function calls itself to
                    solve subproblems of the same type. While elegant, it requires a
                    deep understanding of <strong>Memory Management</strong> and the
                    <strong>Execution Stack</strong>.
                </p>
            </div>

            <div className="content-section">
                <h2>The Two Pillars of Recursion</h2>
                <p>
                    Every recursive function MUST have two parts. Without them, you either
                    get an infinite loop or a logic error:
                </p>
                <ul>
                    <li><strong>Base Case:</strong> The "Stop" condition that prevents infinite execution.</li>
                    <li><strong>Recursive Step:</strong> The logic that moves the problem closer to the base case.</li>
                </ul>
                <CodeBlock language="javascript" code={basicRecursionCode} />
            </div>

            <div className="content-section">
                <h2>The Call Stack & Memory</h2>
                <p>
                    Each time a function calls itself, the JS engine adds a new
                    <strong>Frame</strong> to the <strong>Call Stack</strong>. This
                    frame consumes memory (storing local variables and the return
                    address).
                </p>
                <CodeBlock language="javascript" code={stackOverflowCode} />
                <Warning><strong> Engineer's Warning:</strong> Most modern browsers limit
                    the call stack to roughly 10,000 frames. If your algorithm
                    processes deeper data, recursion will crash your application with
                    a <code>RangeError</code>.</Warning>
            </div>

            <div className="content-section">
                <h2>Tail Call Optimization (TCO)</h2>
                <p>
                    In theory, <strong>Tail Recursion</strong> allows an engine to reuse
                    the current stack frame for the next call, enabling "infinite"
                    recursion depth.
                </p>
                <ProTip><strong> High-Level Insight:</strong> While TCO is part of the
                    ES6 specification, <strong>most JS engines (except Safari/WebKit)
                        do not implement it</strong> due to debugging and complexity
                    concerns. In Chrome (V8) or Node.js, tail recursion still
                    consumes stack space.</ProTip>
            </div>

            <div className="content-section">
                <h2>Bypassing Limits: The Trampoline</h2>
                <p>
                    When you need recursive clarity for massive depth in JavaScript,
                    the <strong>Trampoline Pattern</strong> is the professional
                    solution. It wraps recursion in a <code>while</code> loop, flattening
                    the stack calls.
                </p>
                <CodeBlock language="javascript" code={trampolinePatternCode} />
            </div>

            <div className="content-section">
                <h2>Where Recursion Shines</h2>
                <p>
                    Recursion is superior to iteration when dealing with
                    <strong>Non-Linear Data Structures</strong> like Trees,
                    Graphs, or Nested Objects (JSON).
                </p>
                <CodeBlock language="javascript" code={treeTraversalCode} />
            </div>

            <KeyFacts><ul>
                    <li><strong>Elegance:</strong> Use recursion for hierarchical data (DOM, File systems).</li>
                    <li> <strong>Stack Limits:</strong> Never use simple recursion for potentially deep operations in V8.</li>
                    <li><strong>Patterns:</strong> Use the <strong>Accumulator Pattern</strong> to prepare for tail-call logic.</li>
                    <li><strong>Optimization:</strong> Use <strong>Memoization</strong> to avoid the exponential time complexity of overlapping subproblems (like Fibonacci).</li>
                    <li><strong>Safety:</strong> Implement <strong>Trampolining</strong> for mission-critical deep traversal.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSRecursion;


